import { eventsService, Event } from '../services/firebase/events.service';
import { subscriptionService, SubscriptionType } from '../services/subscription/subscription.service';
import { userService } from '../services/firebase/user.service';

export interface EventValidationResult {
  isValid: boolean;
  reason?: string;
  currentEventCount: number;
  maxAllowed: number;
  subscription: SubscriptionType;
  canCreateMore: boolean;
  hasExistingEvents: boolean;
  existingEvents: Event[];
  suggestedAction?: string;
}

/**
 * Valide si l'utilisateur peut créer un nouvel événement
 */
export const validateEventCreation = async (userId?: string): Promise<EventValidationResult> => {
  try {
    // Obtenir l'utilisateur courant si pas fourni
    const user = userId ? { uid: userId } : await userService.getCurrentUser();
    
    if (!user) {
      return {
        isValid: false,
        reason: 'Utilisateur non connecté',
        currentEventCount: 0,
        maxAllowed: 0,
        subscription: 'free',
        canCreateMore: false,
        hasExistingEvents: false,
        existingEvents: [],
        suggestedAction: 'Veuillez vous connecter'
      };
    }

    // Obtenir les événements existants
    const existingEvents = await eventsService.getUserEvents(user.uid);
    const currentEventCount = existingEvents.length;
    const hasExistingEvents = currentEventCount > 0;

    // Obtenir l'abonnement
    const subscription = await subscriptionService.getCurrentSubscription(user.uid);
    const maxAllowed = subscription.maxEvents;

    // Vérifications de base
    if (currentEventCount >= maxAllowed) {
      return {
        isValid: false,
        reason: `Limite d'événements atteinte (${currentEventCount}/${maxAllowed})`,
        currentEventCount,
        maxAllowed,
        subscription: subscription.type,
        canCreateMore: false,
        hasExistingEvents,
        existingEvents,
        suggestedAction: subscription.type === 'free' 
          ? 'Passez au plan Essential (3 événements) ou Pro (illimité)'
          : subscription.type === 'essential'
          ? 'Passez au plan Pro pour des événements illimités'
          : 'Limite inattendue atteinte'
      };
    }

    // Vérification de la fonctionnalité multi-événements
    if (hasExistingEvents && !subscription.features.multipleEvents) {
      return {
        isValid: false,
        reason: 'Votre abonnement ne permet pas plusieurs événements',
        currentEventCount,
        maxAllowed,
        subscription: subscription.type,
        canCreateMore: false,
        hasExistingEvents,
        existingEvents,
        suggestedAction: 'Passez au plan Essential ou Pro pour gérer plusieurs événements'
      };
    }

    // Validation réussie
    return {
      isValid: true,
      currentEventCount,
      maxAllowed,
      subscription: subscription.type,
      canCreateMore: true,
      hasExistingEvents,
      existingEvents
    };

  } catch (error) {
    console.error('Erreur lors de la validation de création d\'événement:', error);
    return {
      isValid: false,
      reason: 'Erreur technique lors de la validation',
      currentEventCount: 0,
      maxAllowed: 0,
      subscription: 'free',
      canCreateMore: false,
      hasExistingEvents: false,
      existingEvents: [],
      suggestedAction: 'Veuillez réessayer'
    };
  }
};

/**
 * Valide si l'utilisateur peut supprimer un événement
 */
export const validateEventDeletion = async (eventId: string, userId?: string): Promise<EventValidationResult> => {
  try {
    const user = userId ? { uid: userId } : await userService.getCurrentUser();
    
    if (!user) {
      return {
        isValid: false,
        reason: 'Utilisateur non connecté',
        currentEventCount: 0,
        maxAllowed: 0,
        subscription: 'free',
        canCreateMore: false,
        hasExistingEvents: false,
        existingEvents: []
      };
    }

    const existingEvents = await eventsService.getUserEvents(user.uid);
    const eventToDelete = existingEvents.find(e => e.id === eventId);
    const subscription = await subscriptionService.getCurrentSubscription(user.uid);

    if (!eventToDelete) {
      return {
        isValid: false,
        reason: 'Événement non trouvé',
        currentEventCount: existingEvents.length,
        maxAllowed: subscription?.maxEvents || 1,
        subscription: subscription?.type || 'free',
        canCreateMore: false,
        hasExistingEvents: existingEvents.length > 0,
        existingEvents
      };
    }

    // Vérifier si c'est l'événement par défaut
    if (eventToDelete.isDefault && existingEvents.length > 1) {
      return {
        isValid: false,
        reason: 'Impossible de supprimer l\'événement par défaut',
        currentEventCount: existingEvents.length,
        maxAllowed: subscription.maxEvents,
        subscription: subscription.type,
        canCreateMore: false,
        hasExistingEvents: true,
        existingEvents,
        suggestedAction: 'Définissez d\'abord un autre événement comme événement par défaut'
      };
    }

    // Ne pas permettre de supprimer le dernier événement
    if (existingEvents.length === 1) {
      return {
        isValid: false,
        reason: 'Impossible de supprimer le dernier événement',
        currentEventCount: existingEvents.length,
        maxAllowed: subscription.maxEvents,
        subscription: subscription.type,
        canCreateMore: false,
        hasExistingEvents: true,
        existingEvents,
        suggestedAction: 'Créez un nouvel événement avant de supprimer celui-ci'
      };
    }

    return {
      isValid: true,
      currentEventCount: existingEvents.length,
      maxAllowed: subscription.maxEvents,
      subscription: subscription.type,
      canCreateMore: true,
      hasExistingEvents: true,
      existingEvents
    };

  } catch (error) {
    console.error('Erreur lors de la validation de suppression d\'événement:', error);
    return {
      isValid: false,
      reason: 'Erreur technique lors de la validation',
      currentEventCount: 0,
      maxAllowed: 0,
      subscription: 'free',
      canCreateMore: false,
      hasExistingEvents: false,
      existingEvents: []
    };
  }
};

/**
 * Détecte les événements en double (même nom, même type, même date)
 */
export const detectDuplicateEvents = async (
  eventData: { name: string; type: string; date?: string | null }, 
  userId?: string
): Promise<{ hasDuplicates: boolean; duplicates: Event[]; suggestion?: string }> => {
  try {
    const user = userId ? { uid: userId } : await userService.getCurrentUser();
    
    if (!user) {
      return { hasDuplicates: false, duplicates: [] };
    }

    const existingEvents = await eventsService.getUserEvents(user.uid);
    
    // Rechercher des événements similaires
    const duplicates = existingEvents.filter(event => {
      const nameMatch = event.name.toLowerCase().trim() === eventData.name.toLowerCase().trim();
      const typeMatch = event.type === eventData.type;
      const dateMatch = event.date === eventData.date || 
        (event.date && eventData.date && 
         new Date(event.date).toDateString() === new Date(eventData.date).toDateString());
      
      // Considéré comme un doublon si au moins 2 critères correspondent
      const matches = [nameMatch, typeMatch, dateMatch].filter(Boolean).length;
      return matches >= 2;
    });

    const hasDuplicates = duplicates.length > 0;
    let suggestion = '';
    
    if (hasDuplicates) {
      suggestion = duplicates.length === 1 
        ? `Un événement similaire existe déjà: "${duplicates[0]?.name || 'Sans nom'}"`
        : `${duplicates.length} événements similaires existent déjà`;
    }

    return { hasDuplicates, duplicates, suggestion };

  } catch (error) {
    console.error('Erreur lors de la détection des doublons:', error);
    return { hasDuplicates: false, duplicates: [] };
  }
};

/**
 * Vérifie l'intégrité générale du compte événements
 */
export const validateAccountEventIntegrity = async (userId?: string): Promise<{
  isHealthy: boolean;
  issues: string[];
  suggestions: string[];
  eventCount: number;
  hasDefaultEvent: boolean;
}> => {
  try {
    const user = userId ? { uid: userId } : await userService.getCurrentUser();
    
    if (!user) {
      return {
        isHealthy: false,
        issues: ['Utilisateur non connecté'],
        suggestions: ['Veuillez vous connecter'],
        eventCount: 0,
        hasDefaultEvent: false
      };
    }

    const existingEvents = await eventsService.getUserEvents(user.uid);
    const issues: string[] = [];
    const suggestions: string[] = [];
    
    // Vérifier s'il y a des événements
    if (existingEvents.length === 0) {
      issues.push('Aucun événement créé');
      suggestions.push('Créez votre premier événement pour commencer');
    }

    // Vérifier s'il y a un événement par défaut
    const defaultEvent = existingEvents.find(e => e.isDefault);
    const hasDefaultEvent = !!defaultEvent;
    
    if (existingEvents.length > 0 && !hasDefaultEvent) {
      issues.push('Aucun événement par défaut défini');
      suggestions.push('Définissez un événement comme événement par défaut');
    }

    // Vérifier les doublons multiples d'événements par défaut
    const defaultEvents = existingEvents.filter(e => e.isDefault);
    if (defaultEvents.length > 1) {
      issues.push(`${defaultEvents.length} événements définis comme "par défaut"`);
      suggestions.push('Un seul événement doit être défini comme événement par défaut');
    }

    // Vérifier les événements sans nom
    const eventsWithoutName = existingEvents.filter(e => !e.name || e.name.trim() === '');
    if (eventsWithoutName.length > 0) {
      issues.push(`${eventsWithoutName.length} événement(s) sans nom`);
      suggestions.push('Donnez un nom à tous vos événements');
    }

    const isHealthy = issues.length === 0;

    return {
      isHealthy,
      issues,
      suggestions,
      eventCount: existingEvents.length,
      hasDefaultEvent
    };

  } catch (error) {
    console.error('Erreur lors de la validation de l\'intégrité du compte:', error);
    return {
      isHealthy: false,
      issues: ['Erreur technique lors de la validation'],
      suggestions: ['Veuillez réessayer ou contacter le support'],
      eventCount: 0,
      hasDefaultEvent: false
    };
  }
}; 